<template>
  <div class="editor-page-bar">
    <div class="title-wrapper">
      <span class="title-text">页面列表</span>
    </div>
    <div class="page-wrapper">
      <ScrollBar :scrollbar-offset="10">
        <PageListItem
          v-for="item in pages"
          :key="item.id"
          :page-data="item"
          :selected="currentPageId === item.id"
          :source-canvas="thumbnailMap[item.id] ?? null"
          :deletable="item.type === 'backCover' && isPaidUser"
          @select="projectStore.setCurrentPage(item.id)"
          @delete="removePage(item.id)"
        />
      </ScrollBar>
    </div>
    <div class="footer-wrapper">
      <span class="footer-text">{{ currentPageLabel }} / 共 {{ pages.length }} 页</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, reactive } from 'vue';
  import ScrollBar from '@/components/common/list/ScrollBar.vue';
  import PageListItem from '@/components/common/list/item/PageListItem.vue';
  import { useProjectStore, useUserStore } from '@/store';
  import { storeToRefs } from 'pinia';
  import { emitter } from '@/utils';
  import { EVENT_NAMES } from '@/constants';

  const projectStore = useProjectStore();
  const userStore = useUserStore();

  const { pages, currentPageId } = storeToRefs(projectStore);

  /** 是否为付费用户（非免费用户） */
  const isPaidUser = computed(() => !userStore.isFreeUser);

  // 缩略图同步：父组件统一监听，按 pageId 路由到对应子组件
  const thumbnailMap = reactive<Record<string, HTMLCanvasElement | null>>({});

  function handleBookPageSync({
    pageId,
    sourceCanvas
  }: {
    pageId: string;
    sourceCanvas: HTMLCanvasElement;
  }) {
    thumbnailMap[pageId] = sourceCanvas;
  }

  onMounted(() => {
    emitter.on(EVENT_NAMES.BOOK_PAGE_THUMB_SYNC, handleBookPageSync);
  });

  onUnmounted(() => {
    emitter.off(EVENT_NAMES.BOOK_PAGE_THUMB_SYNC, handleBookPageSync);
  });

  const currentPageLabel = computed(() => {
    const current = pages.value.find((p) => p.id === currentPageId.value);
    if (!current) return '封面';
    if (current.type === 'cover') return '封面';
    if (current.type === 'backCover') return '封底';
    if (current.index !== undefined) return String(current.index).padStart(2, '0');
    return '封面';
  });

  function removePage(id: string) {
    const page = pages.value.find((p) => p.id === id);
    // 仅尾页（封底）且付费用户可删除
    if (page?.type !== 'backCover' || !isPaidUser.value) return;
    projectStore.removePage(id);
  }
</script>

<style scoped>
  .editor-page-bar {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background-color: #ffffff;
    border-right: 1px solid #f1f5f9;
    box-shadow: 2px 0px 12px 0px rgba(0, 0, 0, 0.03);
  }

  .title-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 12px 16px 8px;
    box-sizing: border-box;
  }

  .title-text {
    font-family: Inter, sans-serif;
    font-weight: 600;
    font-size: 11px;
    line-height: 16.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #94a3b8;
  }

  .page-wrapper {
    width: 100%;
    height: 0;
    flex: 1;
    padding: 10px;
    box-sizing: border-box;
  }

  .footer-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 12px 16px;
    box-sizing: border-box;
    border-top: 1px solid #f0f0f0;
  }

  .footer-text {
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 11px;
    line-height: 16.5px;
    color: #94a3b8;
  }
</style>
